import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { PerformanceTeamOverview } from 'app/library/performance-team-review/models/performance-team-overview.model';
import { PerformanceTeamReviewService } from 'app/library/performance-team-review/services/performance-team-review.service';
import { TeamCertificationDialogComponent } from 'app/library/team/components/dialogs/team-certification-dialog/team-certification-dialog.component';
import { TeamCertificationParentNameEnum } from 'app/library/team/enums/team-certification-dialog-type.enum';
import { IAddTeamMembersActionPayload } from 'app/library/team/models/add-team-members-action.action.payload';
import { TeamBookingOverview } from 'app/library/team/models/team-booking-overview.model';
import { Team } from 'app/library/team/models/team.model';
import { TeamBookingOverviewApiService } from 'app/library/team/services/team-booking-overview.api-service';
import { TeamService } from 'app/library/team/services/team.service';
import { TrackTeamSubTeamProgressService } from 'app/library/team/services/track-team-subteam-progress.service';
import { TrainingApprovalTeamRequestsService } from 'app/library/training-approval-team-requests/services/training-approval-team-requests.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddUserToTeamDialogComponent } from '../../dialogs/add-user-to-team-dialog/add-user-to-team-dialog.component';

@Component({
    selector: 'app-team',
    templateUrl: './team.component.html',
    styleUrls: ['./team.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(AllUsersState.getEntities)
    allUser$: Observable<User[]>;

    @Select(AllUsersState.getEntities)
    filteredAllUsers$: Observable<User[]>;

    @Input()
    isTeamDetailsPage: boolean;

    currentTeam: Team;
    teamId: string;

    isCurrentUserTenantAdmin: boolean;
    isCertificateMgmtFeatureEnabled: boolean = false;
    isTrainingApprovalRequestsFeatureEnabled: boolean = false;
    isPerformanceReviewFeatureActive: boolean;
    isCourseCatalogFeatureEnabled: boolean;
    profile = this._store.selectSnapshot(UserState.getMyInfo);

    private _hasTenantManagement: boolean = false;
    private _hasCertificationWrite: boolean = false;

    isTrackSubTeamProgressActive = new FormControl();

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    addUserToTeamDialogRef: MatDialogRef<AddUserToTeamDialogComponent>;

    @Output()
    teamProgressChanged = new EventEmitter<boolean>();

    performanceTeamOverview: PerformanceTeamOverview;
    approvalRequestsTeamOverview: { inProgressRequests: number };
    bookingsTeamOverview: TeamBookingOverview;

    private get isCurrentUserTeamLead(): boolean {
        return !!this.currentTeam && (this.currentTeam.isCurrentUserLeader || this.currentTeam.isCurrentUserTeamLeaderSubteams);
    }

    private get isCurrentUserTrainingResponsible(): boolean {
        return (
            !!this.currentTeam &&
            (this.currentTeam.isCurrentUserTrainingResponsible || this.currentTeam.isCurrentUserUpperTrainingResponsible)
        );
    }

    get canViewCertificates(): boolean {
        return this.isCurrentUserTeamLead || this.isCurrentUserTrainingResponsible || this._hasTenantManagement || this._hasCertificationWrite;
    }

    get canViewBookings(): boolean {
        return this.isCurrentUserTeamLead || this.isCurrentUserTrainingResponsible || this._hasTenantManagement;
    }

    get canViewApprovalRequests(): boolean {
        return this.isCurrentUserTeamLead || this._hasTenantManagement;
    }

    get isPerformanceReviewTileVisible(): boolean {
        return (
            this.isCurrentUserTeamLead ||
            this.profile.permissions.includes(AdminPermissions.PERFORMANCE_REVIEW_WRITE) ||
            this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        );
    }

    get isPerformanceReviewTileClickable(): boolean {
        return (
            this.isCurrentUserTeamLead ||
            this.profile.permissions.includes(AdminPermissions.PERFORMANCE_REVIEW_WRITE) ||
            this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        );
    }

    constructor(
        public sideNav: SidenavService,
        public teamService: TeamService,
        private _trackTeamSubTeamProgressService: TrackTeamSubTeamProgressService,
        private _performanceTeamReviewService: PerformanceTeamReviewService,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _matDialog: MatDialog,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _store: Store,
        private _trainingApprovalTeamRequestsService: TrainingApprovalTeamRequestsService,
        private _teamBookingOverviewApiService: TeamBookingOverviewApiService
    ) {
        super();

        this.isPerformanceReviewFeatureActive = this._featureSwitchPipe.transform(FeatureSwitchName.PerformanceFeedbackForm);
        this.isCertificateMgmtFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateManagement);
        this.isTrainingApprovalRequestsFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.TrainingApprovalRequest);
        this.isCourseCatalogFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CourseCatalog);

        const permissions = this.profile.permissions;
        this._hasTenantManagement = permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        this._hasCertificationWrite = permissions.includes(AdminPermissions.CERTIFICATION_WRITE);
    }

    async ngOnInit(): Promise<void> {
        this.teamService.team$.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (team) => {
            if (team) {
                this.currentTeam = team;
                this.teamId = team.id;
                const includeSubTeamsProgress = !this._trackTeamSubTeamProgressService.isTrackTeamSubTeamProgressExcluded(team.id);
                this.isTrackSubTeamProgressActive.setValue(includeSubTeamsProgress);

                this.isCurrentUserTenantAdmin = this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);

                if (this.isPerformanceReviewFeatureActive && this.isPerformanceReviewTileVisible) {
                    this.performanceTeamOverview = await this._performanceTeamReviewService.getPerformanceTeamReviewOverview(this.teamId);
                }

                if (this.isTrainingApprovalRequestsFeatureEnabled) {
                    this.approvalRequestsTeamOverview = await this._trainingApprovalTeamRequestsService.getTrainingApprovalTeamRequestsOverview(
                        this.teamId,
                        includeSubTeamsProgress
                    );
                }

                if (this.isCourseCatalogFeatureEnabled) {
                    await this._fetchBookingsOverview(includeSubTeamsProgress);
                }
            }
        });
    }

    toggleTeamProgressChanged(event: MatSlideToggleChange): void {
        if (event.checked) {
            this._trackTeamSubTeamProgressService.removeExcludedTeam(this.teamId);
        } else {
            this._trackTeamSubTeamProgressService.addExcludedTeam(this.teamId);
        }
        this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, event.checked);
        this.teamProgressChanged.emit(event.checked);

        if (this.isCourseCatalogFeatureEnabled) {
            void this._fetchBookingsOverview(event.checked);
        }
    }

    private async _fetchBookingsOverview(includeSubteams: boolean): Promise<void> {
        try {
            this.bookingsTeamOverview = await this._teamBookingOverviewApiService.getTeamBookingOverview(this.teamId, includeSubteams).toPromise();
        } catch {
            this.bookingsTeamOverview = null;
        }
    }

    // Method to open the appropriate dialog based on isTeamDetailsPage
    openAddDialog(): void {
        if (this.isTeamDetailsPage) {
            this.openAddUserToTeamDialog();
        } else {
            this.openAddMemberDialog();
        }
    }

    openAddMemberDialog(): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.teamId;
        dialogRef.componentInstance.title = 'TEAMS.MEMBER.BUTTON.ADD_MEMBER';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'teamMember';

        dialogRef.afterClosed().subscribe(async (result: { list: User[] }) => {
            if (result && result.list) {
                const payload: IAddTeamMembersActionPayload = {
                    userIds: result.list.map((user) => user.id),
                    teamIds: [this.teamId],
                };

                await this.teamService.addUsersToTeamAsMembers(payload);
                await this.teamService.fetchTeamMembers(this.teamId);
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive.value);
            }
        });
    }

    async openAddUserToTeamDialog(): Promise<void> {
        await this.teamService.fetchTeamLeaderTeams();

        this.addUserToTeamDialogRef = this._matDialog.open(AddUserToTeamDialogComponent, {
            disableClose: false,
            width: '900px',
            autoFocus: false,
        });

        const teams = this.teamService.getTeamLeaderTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        const teamListTree = this.teamService.convertListToTree(clonedTeams);

        this.addUserToTeamDialogRef.componentInstance.entities$ = this.filteredAllUsers$;
        this.addUserToTeamDialogRef.componentInstance.title = this._translateService.instant('TEAMS.MEMBER.BUTTON.ADD_MEMBER');
        this.addUserToTeamDialogRef.componentInstance.teamId = this.teamId;
        this.addUserToTeamDialogRef.componentInstance.role = 'teamMember';
        this.addUserToTeamDialogRef.componentInstance.listTree = teamListTree;

        this.addUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedNodes: Set<User> }) => {
            if (data) {
                const userIds: string[] = Array.from(data.selectedNodes).map((user) => user.id);

                const payload: IAddTeamMembersActionPayload = {
                    userIds: userIds,
                    teamIds: [this.teamId],
                };

                const result = await this.teamService.addUsersToTeamAsMembers(payload);
                await this.teamService.fetchTeamMembers(this.teamId);
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive.value);

                if (
                    result &&
                    (result.asyncJobId || (result.teamMembers && result.teamMembers.length > 0))
                ) {
                    const successMessage = this._translateService.instant('TEAMS.DIALOG.ADD_USER_TO_TEAM.MEMBER.MESSAGE.SUCCESS');

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
            this.addUserToTeamDialogRef = null;
        });
    }

    openTeamCertificatesDialog(): void {
        const dialogRef = this._matDialog.open(TeamCertificationDialogComponent, {
            disableClose: false,
            width: '1244px',
        });

        dialogRef.componentInstance.title = 'PAGE.CERTIFICATIONS.TITLE';
        dialogRef.componentInstance.parentType = TeamCertificationParentNameEnum.Team;
        dialogRef.componentInstance.team = this.currentTeam;
        dialogRef.componentInstance.isTrackSubTeamProgressActive = this.isTrackSubTeamProgressActive.value;
    }
}
